import React, { useState, useCallback, useRef } from "react";
import { IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { exportTimesheetReportExcel } from "../util/timesheet-report-excel";
import { computeReportTotals } from "../util/timesheet-breakdown";
import CustomTable from "../../../components/table/table";
import { numberFormat } from "../../../util/currency-formatter";

/**
 * Timesheet Report Table component reusing CustomTable with expandable project-wise
 * breakdown rows, footer totals, and two-sheet Excel export.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.data - Aggregated user report rows
 * @param {React.ReactNode} [props.toolbarLeftContent] - Optional left toolbar content
 * @returns {React.ReactElement} TimesheetReportTable component
 */
const TimesheetReportTable = ({ data = [], toolbarLeftContent = null }) => {
  const [expandedUsers, setExpandedUsers] = useState({});
  const [collapsingUsers, setCollapsingUsers] = useState({});
  const lastToggleTimeRef = useRef({});

  /**
   * Toggles the expanded state for a specific user row with smooth entrance and exit animations.
   *
   * @param {string} userId - User identifier
   * @param {React.MouseEvent} [event] - Click event
   * @returns {void}
   */
  const handleToggleExpand = useCallback((userId, event) => {
    if (event && typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
    if (!userId) return;

    const now = Date.now();
    if (
      lastToggleTimeRef.current[userId] &&
      now - lastToggleTimeRef.current[userId] < 350
    ) {
      return;
    }
    lastToggleTimeRef.current[userId] = now;

    setExpandedUsers((prev) => {
      const isCurrentlyExpanded = Boolean(prev[userId]);
      if (isCurrentlyExpanded) {
        setCollapsingUsers((cPrev) => ({ ...cPrev, [userId]: true }));
        setTimeout(() => {
          setExpandedUsers((latest) => {
            const next = { ...latest };
            delete next[userId];
            return next;
          });
          setCollapsingUsers((cPrev) => {
            const next = { ...cPrev };
            delete next[userId];
            return next;
          });
        }, 220);
        return prev;
      }

      setCollapsingUsers((cPrev) => {
        const next = { ...cPrev };
        delete next[userId];
        return next;
      });
      return {
        ...prev,
        [userId]: true,
      };
    });
  }, []);

  /**
   * Exports a styled two-sheet Excel (.xlsx) file containing User Summary (Sheet 1)
   * and Project Breakdown (Sheet 2).
   *
   * @param {Array<Object>} visibleRows - Currently filtered/visible report rows
   * @returns {Promise<void>} Resolves once the download is triggered
   */
  const handleExportExcel = useCallback(
    (visibleRows = []) => {
      const rowsToExport = Array.isArray(visibleRows) ? visibleRows : data;
      return exportTimesheetReportExcel(
        rowsToExport,
        computeReportTotals(rowsToExport)
      );
    },
    [data]
  );

  const renderClickableCell = (content, rowData, customClass = "") => (
    <div
      className={`timesheet-report-cell-action ${customClass}`}
      onClick={(e) => {
        e.stopPropagation();
        handleToggleExpand(rowData.userId);
      }}
    >
      {content}
    </div>
  );

  const columns = [
    {
      title: "",
      field: "expand",
      class: "timesheet-report-expand-col",
      render: (rowData) => {
        const isExpanded = Boolean(expandedUsers[rowData.userId]);
        const isCollapsing = Boolean(collapsingUsers[rowData.userId]);
        const isCurrentlyOpen = isExpanded && !isCollapsing;
        return (
          <IconButton
            size="small"
            className={`timesheet-report-expand-btn ${isCurrentlyOpen ? "expanded" : ""}`}
            onClick={(event) => handleToggleExpand(rowData.userId, event)}
            data-cy={`expand-report-row-${rowData.userId}`}
            aria-label={isCurrentlyOpen ? "Collapse project breakdown" : "Expand project breakdown"}
          >
            <KeyboardArrowDownIcon fontSize="small" className="timesheet-expand-icon" />
          </IconButton>
        );
      },
    },
    {
      title: "Emp Name",
      field: "empName",
      render: (rowData) =>
        renderClickableCell(
          <div className="timesheet-report-emp-cell">
            <span className="timesheet-report-emp-name">{rowData.empName}</span>
            {rowData.isGlobal && (
              <span className="timesheet-global-user-tag"> (Global User)</span>
            )}
          </div>,
          rowData
        ),
    },
    {
      title: "Total Entries",
      field: "totalEntries",
      class: "text-center",
      render: (rowData) =>
        renderClickableCell(rowData.totalEntries, rowData, "text-center"),
    },
    {
      title: "Standard Hours",
      field: "standardHours",
      class: "text-end",
      render: (rowData) =>
        renderClickableCell(
          numberFormat(rowData.standardHours, true),
          rowData,
          "text-end"
        ),
    },
    {
      title: "Standard Cost",
      field: "standardCost",
      class: "text-end",
      render: (rowData) =>
        renderClickableCell(
          numberFormat(rowData.standardCost),
          rowData,
          "text-end"
        ),
    },
    {
      title: "Overtime Hours",
      field: "overtimeHours",
      class: "text-end",
      render: (rowData) =>
        renderClickableCell(
          numberFormat(rowData.overtimeHours, true),
          rowData,
          "text-end"
        ),
    },
    {
      title: "Overtime Cost",
      field: "overtimeCost",
      class: "text-end",
      render: (rowData) =>
        renderClickableCell(
          numberFormat(rowData.overtimeCost),
          rowData,
          "text-end"
        ),
    },
    {
      title: "Holiday Hours",
      field: "holidayHours",
      class: "text-end",
      render: (rowData) =>
        renderClickableCell(
          numberFormat(rowData.holidayHours, true),
          rowData,
          "text-end"
        ),
    },
    {
      title: "Holiday Cost",
      field: "holidayCost",
      class: "text-end",
      render: (rowData) =>
        renderClickableCell(
          numberFormat(rowData.holidayCost),
          rowData,
          "text-end"
        ),
    },
    {
      title: "Total Hours",
      field: "totalHours",
      class: "text-end fw-bold",
      render: (rowData) =>
        renderClickableCell(
          <strong>{numberFormat(rowData.totalHours, true)}</strong>,
          rowData,
          "text-end fw-bold"
        ),
    },
    {
      title: "Total Cost",
      field: "totalCost",
      class: "text-end fw-bold",
      render: (rowData) =>
        renderClickableCell(
          <strong>{numberFormat(rowData.totalCost)}</strong>,
          rowData,
          "text-end fw-bold"
        ),
    },
  ];

  /**
   * Renders expanded project-wise breakdown sub-rows directly in the parent table body
   * with smooth entrance and exit animations.
   *
   * @param {Object} userRow - Parent user summary row
   * @returns {React.ReactNode} Expanded sub-header and project rows
   */
  const renderExpandedRow = (userRow) => {
    const isExpanded = Boolean(expandedUsers[userRow.userId]);
    const isCollapsing = Boolean(collapsingUsers[userRow.userId]);

    if (!isExpanded && !isCollapsing) {
      return null;
    }

    const projects = Array.isArray(userRow.projects) ? userRow.projects : [];
    const animClass = isCollapsing
      ? "timesheet-report-collapsing"
      : "timesheet-report-expanding";

    return (
      <React.Fragment>
        <tr className={`timesheet-report-subheader-row ${animClass}`}>
          <td colSpan={2}>
            <div className="timesheet-anim-cell">Project Name</div>
          </td>
          <td className="text-center">
            <div className="timesheet-anim-cell">Total Entries</div>
          </td>
          <td className="text-end">
            <div className="timesheet-anim-cell">Standard Hours</div>
          </td>
          <td className="text-end">
            <div className="timesheet-anim-cell">Standard Cost</div>
          </td>
          <td className="text-end">
            <div className="timesheet-anim-cell">Overtime Hours</div>
          </td>
          <td className="text-end">
            <div className="timesheet-anim-cell">Overtime Cost</div>
          </td>
          <td className="text-end">
            <div className="timesheet-anim-cell">Holiday Hours</div>
          </td>
          <td className="text-end">
            <div className="timesheet-anim-cell">Holiday Cost</div>
          </td>
          <td className="text-end">
            <div className="timesheet-anim-cell">Total Hours</div>
          </td>
          <td className="text-end">
            <div className="timesheet-anim-cell">Total Cost</div>
          </td>
        </tr>
        {projects.map((proj) => (
          <tr
            key={`${userRow.userId}-${proj.projectId}`}
            className={`timesheet-report-project-row ${animClass}`}
          >
            <td colSpan={2}>
              <div className="timesheet-anim-cell timesheet-report-project-name">
                <span className="timesheet-report-project-dot" />
                <span>{proj.projectName}</span>
              </div>
            </td>
            <td className="text-center">
              <div className="timesheet-anim-cell">{proj.totalEntries}</div>
            </td>
            <td className="text-end">
              <div className="timesheet-anim-cell">
                {numberFormat(proj.standardHours, true)}
              </div>
            </td>
            <td className="text-end">
              <div className="timesheet-anim-cell">
                {numberFormat(proj.standardCost)}
              </div>
            </td>
            <td className="text-end">
              <div className="timesheet-anim-cell">
                {numberFormat(proj.overtimeHours, true)}
              </div>
            </td>
            <td className="text-end">
              <div className="timesheet-anim-cell">
                {numberFormat(proj.overtimeCost)}
              </div>
            </td>
            <td className="text-end">
              <div className="timesheet-anim-cell">
                {numberFormat(proj.holidayHours, true)}
              </div>
            </td>
            <td className="text-end">
              <div className="timesheet-anim-cell">
                {numberFormat(proj.holidayCost)}
              </div>
            </td>
            <td className="text-end fw-bold">
              <div className="timesheet-anim-cell">
                {numberFormat(proj.totalHours, true)}
              </div>
            </td>
            <td className="text-end fw-bold">
              <div className="timesheet-anim-cell">
                {numberFormat(proj.totalCost)}
              </div>
            </td>
          </tr>
        ))}
      </React.Fragment>
    );
  };

  /**
   * Renders the bottom TOTAL summary row inside CustomTable tbody.
   *
   * @param {Array<Object>} visibleRows - Rows currently active in CustomTable
   * @returns {React.ReactNode} Summary footer row
   */
  const renderFooterRow = (visibleRows) => {
    const totals = computeReportTotals(visibleRows);
    return (
      <tr className="timesheet-report-total-row">
        <td colSpan={2} className="fw-bold">
          TOTAL
        </td>
        <td className="text-center fw-bold">{totals.totalEntries}</td>
        <td className="text-end fw-bold">
          {numberFormat(totals.standardHours, true)}
        </td>
        <td className="text-end fw-bold">{numberFormat(totals.standardCost)}</td>
        <td className="text-end fw-bold">
          {numberFormat(totals.overtimeHours, true)}
        </td>
        <td className="text-end fw-bold">{numberFormat(totals.overtimeCost)}</td>
        <td className="text-end fw-bold">
          {numberFormat(totals.holidayHours, true)}
        </td>
        <td className="text-end fw-bold">{numberFormat(totals.holidayCost)}</td>
        <td className="text-end fw-bold">
          {numberFormat(totals.totalHours, true)}
        </td>
        <td className="text-end fw-bold">{numberFormat(totals.totalCost)}</td>
      </tr>
    );
  };

  return (
    <div className="timesheet-report-table-wrapper">
      <CustomTable
        columns={columns}
        data={data}
        isAction={false}
        isExport={true}
        onExport={handleExportExcel}
        fileName="Timesheet_Report"
        renderExpandedRow={renderExpandedRow}
        rowClickHandler={(row) => handleToggleExpand(row?.userId)}
        footerRow={renderFooterRow}
        toolbarLeftContent={toolbarLeftContent}
      />
    </div>
  );
};

export default TimesheetReportTable;
